/**
 * useIdentityStore — Identities with per-identity timer settings.
 *
 * Storage: 'identities' (collection), 'active_identity' (single)
 * Methods: addIdentity, updateIdentity, deleteIdentity, setActive
 * Cross-store: read by useTimerStore.nextPhase() for phase-skip settings
 */
import { create } from 'zustand';
import { storage, onRehydrate } from '../utils/storage';

const STORAGE_KEY = 'identities';
const ACTIVE_KEY = 'active_identity';

const useIdentityStore = create((set, get) => ({
    identities: storage.get(STORAGE_KEY, []),
    activeIdentityId: storage.get(ACTIVE_KEY, null),

    getActiveIdentity() {
        const { identities, activeIdentityId } = get();
        return identities.find(i => i.id === activeIdentityId) || null;
    },

    addIdentity(identity) {
        const newIdentity = {
            ...identity,
            id: crypto.randomUUID(),
            createdAt: new Date().toISOString(),
            sortOrder: get().identities.length,
            settings: identity.settings || {
                durations: { focus: 50 * 60, rest: 10 * 60, reflect: 5 * 60, prep: 2 * 60 },
                phases: { prep: true, reflect: true, rest: true }
            }
        };
        set(state => {
            const updated = [...state.identities, newIdentity];
            storage.set(STORAGE_KEY, updated);
            // Auto-select if first identity
            if (updated.length === 1) {
                storage.set(ACTIVE_KEY, newIdentity.id);
                return { identities: updated, activeIdentityId: newIdentity.id };
            }
            return { identities: updated };
        });
        return newIdentity;
    },

    reorderIdentities(orderedIds) {
        set(state => {
            const updated = orderedIds.map((id, i) => {
                const identity = state.identities.find(a => a.id === id);
                return identity ? { ...identity, sortOrder: i } : null;
            }).filter(Boolean);
            storage.set(STORAGE_KEY, updated);
            return { identities: updated };
        });
    },

    updateIdentity(id, updates) {
        set(state => {
            const updated = state.identities.map(i =>
                i.id === id ? { ...i, ...updates } : i
            );
            storage.set(STORAGE_KEY, updated);
            return { identities: updated };
        });
    },

    deleteIdentity(id) {
        set(state => {
            const updated = state.identities.filter(i => i.id !== id);
            storage.set(STORAGE_KEY, updated);
            const newActive = state.activeIdentityId === id
                ? (updated[0]?.id || null)
                : state.activeIdentityId;
            storage.set(ACTIVE_KEY, newActive);
            return { identities: updated, activeIdentityId: newActive };
        });
    },

    setActiveIdentity(id) {
        storage.set(ACTIVE_KEY, id);
        set({ activeIdentityId: id });
    },
}));

// Re-read from localStorage after cloud sync completes
onRehydrate(() => {
    useIdentityStore.setState({
        identities: storage.get(STORAGE_KEY, []),
        activeIdentityId: storage.get(ACTIVE_KEY, null),
    });
});

export default useIdentityStore;
